import React from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Divider,
  Stack,
  Chip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ExitToApp as LogoutIcon,
  AccountCircle,
  Dashboard as DashboardIcon,
  ListAlt as ListAltIcon,
  BarChart as BarChartIcon,
  DonutLarge as DonutLargeIcon,
  Rule as RuleIcon,
  Timer as TimerIcon,
  AdminPanelSettings as AdminIcon,
  Settings as SettingsIcon,
  HelpOutline as HelpIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { layout } from '../theme/tokens';
import Logo from './ui/Logo';
import { buildInfo } from '../buildInfo';

const NAV_SECTIONS = [
  {
    heading: 'Analyse',
    items: [
      { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
      { text: 'Tickets', icon: <ListAltIcon />, path: '/tickets' },
      { text: 'Analytics', icon: <BarChartIcon />, path: '/analytics' },
      { text: 'Allocation', icon: <DonutLargeIcon />, path: '/allocation' },
      { text: 'Work Timer', icon: <TimerIcon />, path: '/timer' },
    ],
  },
  {
    heading: 'Manage',
    items: [
      { text: 'Categorisation', icon: <RuleIcon />, path: '/categorisation' },
      { text: 'Admin', icon: <AdminIcon />, path: '/admin' },
    ],
  },
  {
    heading: 'Support',
    items: [
      { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
      { text: 'Help', icon: <HelpIcon />, path: '/help' },
    ],
  },
];

const BrandMark = () => (
  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 3, height: layout.headerHeight }}>
    <Logo size={30} />
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="subtitle1" noWrap sx={{ lineHeight: 1.2 }}>
        Squid
      </Typography>
      <Typography variant="caption" color="text.secondary" noWrap>
        Engineering analytics
      </Typography>
    </Box>
  </Stack>
);

const NavContent = ({ onNavigate }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
    <BrandMark />
    <Divider />
    <Box sx={{ flex: 1, overflowY: 'auto', py: 2 }}>
      {NAV_SECTIONS.map((section) => (
        <Box key={section.heading} sx={{ mb: 2 }}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ px: 3, display: 'block', mb: 0.5 }}
          >
            {section.heading}
          </Typography>
          <List disablePadding sx={{ px: 1.5 }}>
            {section.items.map((item) => (
              <ListItem key={item.path} disablePadding sx={{ mb: 0.25 }}>
                <ListItemButton
                  component={NavLink}
                  to={item.path}
                  onClick={onNavigate}
                  sx={{
                    borderRadius: 2,
                    py: 0.9,
                    color: 'text.secondary',
                    '& .MuiListItemIcon-root': { minWidth: 36, color: 'inherit' },
                    '& svg': { fontSize: 20 },
                    '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
                    '&.active': {
                      bgcolor: 'action.selected',
                      color: 'primary.light',
                      fontWeight: 600,
                      '& .MuiListItemText-primary': { fontWeight: 600 },
                    },
                  }}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{ fontSize: '0.875rem' }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      ))}
    </Box>
    <Divider />
    <Box sx={{ p: 2 }}>
      <Typography variant="caption" color="text.disabled" noWrap component="div">
        Build {buildInfo?.timestamp ? new Date(buildInfo.timestamp).toLocaleDateString() : 'dev'}
      </Typography>
    </Box>
  </Box>
);

/** Human-readable title for the current route, shown in the app bar. */
const useRouteTitle = () => {
  const { pathname } = useLocation();
  const all = NAV_SECTIONS.flatMap((s) => s.items);
  return all.find((i) => pathname.startsWith(i.path))?.text || 'Squid';
};

const Layout = () => {
  const { account, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const title = useRouteTitle();

  const closeMenu = () => setAnchorEl(null);

  return (
    <Box sx={{ display: 'flex', width: '100%', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        sx={{ zIndex: (t) => t.zIndex.drawer + 1, width: '100%' }}
      >
        <Toolbar sx={{ height: layout.headerHeight }}>
          <IconButton
            color="inherit"
            aria-label="Open navigation"
            edge="start"
            onClick={() => setMobileOpen(true)}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Typography
            variant="h6"
            component="h1"
            noWrap
            sx={{ ml: { md: `${layout.sidebarWidth - 24}px` } }}
          >
            {title}
          </Typography>

          <Box sx={{ flexGrow: 1 }} />

          <Chip
            size="small"
            variant="outlined"
            label={process.env.NODE_ENV === 'development' ? 'Local' : 'Production'}
            sx={{ mr: 2, display: { xs: 'none', sm: 'flex' } }}
          />

          <Tooltip title={account?.name || 'Account'}>
            <IconButton
              onClick={(e) => setAnchorEl(e.currentTarget)}
              size="small"
              aria-label="Open account menu"
              aria-haspopup="true"
              aria-expanded={Boolean(anchorEl)}
            >
              <Avatar
                sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 14, fontWeight: 700 }}
                alt={account?.name || 'User'}
                src={account?.photoUrl}
              >
                {account?.name ? account.name.charAt(0).toUpperCase() : <AccountCircle />}
              </Avatar>
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={closeMenu}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            slotProps={{ paper: { sx: { mt: 1, minWidth: 220 } } }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle2" noWrap>
                {account?.name || 'Signed in'}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {account?.username || 'local development'}
              </Typography>
            </Box>
            <Divider />
            <MenuItem
              onClick={() => {
                closeMenu();
                logout();
              }}
            >
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Sign out
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { md: layout.sidebarWidth }, flexShrink: { md: 0 } }}
        aria-label="Main navigation"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: layout.sidebarWidth, boxSizing: 'border-box' },
          }}
        >
          <NavContent onNavigate={() => setMobileOpen(false)} />
        </Drawer>

        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { width: layout.sidebarWidth, boxSizing: 'border-box' },
          }}
        >
          <NavContent />
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          px: { xs: 2, sm: 3, lg: 4 },
          py: 3,
          mt: `${layout.headerHeight}px`,
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout;
